import React, { useState, useEffect, useRef } from 'react';
import {
  Mail, MessageCircle, Send, Clock, CheckCircle,
  Twitter, Youtube, Instagram, Facebook, ChevronDown,
} from 'lucide-react';

const FadeIn = ({ children, delay = 0, style = {} }) => {
  const [vis, setVis] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => e.isIntersecting && setVis(true), { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{
      opacity: vis ? 1 : 0,
      transform: vis ? 'none' : 'translateY(22px)',
      transition: `opacity 0.7s ${delay}ms ease, transform 0.7s ${delay}ms ease`,
      ...style,
    }}>
      {children}
    </div>
  );
};

const CHANNELS = [
  {
    icon: <Send style={{ width: 22, height: 22 }} />,
    label: 'Telegram',
    desc: 'Join our trading community for real-time signals, alerts, and discussion.',
    cta: 'Join Community →',
    href: 'https://t.me/bullexailabs',
    color: '#38FE60',
    rgb: '56,254,96',
  },
  {
    icon: <MessageCircle style={{ width: 22, height: 22 }} />,
    label: 'Discord',
    desc: 'Chat with traders, get technical support, and connect with the team.',
    cta: 'Open Discord →',
    href: 'https://discord.gg/shdwXNAX',
    color: '#a78bfa',
    rgb: '167,139,250',
  },
  {
    icon: <Mail style={{ width: 22, height: 22 }} />,
    label: 'Email',
    desc: 'For billing, partnerships, or anything requiring a written response.',
    cta: 'support@bullexai.com',
    href: 'mailto:support@bullexai.com',
    color: '#38bdf8',
    rgb: '56,189,248',
  },
];

const ChannelCard = ({ ch, delay }) => {
  const [hov, setHov] = useState(false);
  return (
    <FadeIn delay={delay}>
      <a href={ch.href} target="_blank" rel="noopener noreferrer"
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          display: 'block',
          borderRadius: 22,
          padding: '32px 30px',
          background: 'rgba(255,255,255,0.025)',
          border: `1px solid ${hov ? `rgba(${ch.rgb},0.28)` : 'rgba(255,255,255,0.07)'}`,
          textDecoration: 'none',
          transform: hov ? 'translateY(-4px)' : 'none',
          boxShadow: hov ? `0 20px 56px rgba(0,0,0,0.4), 0 0 0 1px rgba(${ch.rgb},0.1)` : 'none',
          transition: 'all 0.28s ease',
        }}>
                <div style={{
          width: 48, height: 48, borderRadius: 14,
          background: `rgba(${ch.rgb},0.1)`,
          border: `1px solid rgba(${ch.rgb},0.2)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: ch.color, marginBottom: 20,
          transition: 'box-shadow 0.28s ease',
          boxShadow: hov ? `0 0 20px rgba(${ch.rgb},0.25)` : 'none',
        }}>
          {ch.icon}
        </div>
        <p style={{ color: '#e8e8e8', fontSize: 16, fontWeight: 700, marginBottom: 8, letterSpacing: '-0.02em' }}>
          {ch.label}
        </p>
        <p style={{ color: '#555', fontSize: 13, lineHeight: 1.7, marginBottom: 20 }}>
          {ch.desc}
        </p>
        <span style={{ color: ch.color, fontSize: 12, fontWeight: 600, letterSpacing: '0.02em' }}>
          {ch.cta}
        </span>
      </a>
    </FadeIn>
  );
};

const SocialPill = ({ href, icon, label }) => {
  const [hov, setHov] = useState(false);
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 18px', borderRadius: 50,
        background: hov ? 'rgba(56,254,96,0.08)' : 'rgba(255,255,255,0.04)',
        border: `1px solid ${hov ? 'rgba(56,254,96,0.25)' : 'rgba(255,255,255,0.08)'}`,
        color: hov ? '#38FE60' : '#666',
        textDecoration: 'none', fontSize: 12, fontWeight: 600,
        transition: 'all 0.22s ease',
        transform: hov ? 'translateY(-2px)' : 'none',
      }}>
      {icon}
      <span>{label}</span>
    </a>
  );
};

const SUBJECTS = [
  { value: 'general',     label: 'General Inquiry'   },
  { value: 'technical',   label: 'Technical Support' },
  { value: 'billing',     label: 'Billing'           },
  { value: 'partnership', label: 'Partnership'       },
  { value: 'other',       label: 'Other'             },
];

const CustomSelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = SUBJECTS.find(s => s.value === value) || SUBJECTS[0];

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative', userSelect: 'none' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'rgba(255,255,255,0.04)',
          border: `1px solid ${open ? 'rgba(56,254,96,0.45)' : 'rgba(255,255,255,0.08)'}`,
          boxShadow: open ? '0 0 0 3px rgba(56,254,96,0.08)' : 'none',
          borderRadius: 12, padding: '13px 16px',
          color: '#e8e8e8', fontSize: 13, cursor: 'pointer',
          transition: 'border-color 0.2s, box-shadow 0.2s',
        }}
      >
        <span>{selected.label}</span>
        <ChevronDown style={{
          width: 15, height: 15, color: '#555',
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.25s',
          flexShrink: 0,
        }} />
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          background: '#1a1a1c', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 12, overflow: 'hidden',
          zIndex: 50, boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
        }}>
          {SUBJECTS.map(s => (
            <div
              key={s.value}
              onClick={() => { onChange(s.value); setOpen(false); }}
              style={{
                padding: '12px 16px', fontSize: 13, cursor: 'pointer',
                color: s.value === value ? '#38FE60' : '#ccc',
                background: s.value === value ? 'rgba(56,254,96,0.07)' : 'transparent',
                transition: 'background 0.15s, color 0.15s',
              }}
              onMouseEnter={e => { if (s.value !== value) { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#fff'; } }}
              onMouseLeave={e => { if (s.value !== value) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#ccc'; } }}
            >
              {s.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label style={{ display: 'block', color: '#888', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
      {label}
    </label>
    {children}
  </div>
);

const inputBase = {
  width: '100%', boxSizing: 'border-box',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 12,
  padding: '13px 16px',
  color: '#e8e8e8', fontSize: 13,
  fontFamily: "'Space Grotesk', sans-serif",
  outline: 'none',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
};

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: 'general', message: '' });
  const [sent, setSent] = useState(false);
  const [focused, setFocused] = useState(null);

  const onChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = e => {
    e.preventDefault();
    setSent(true);
    setForm({ name: '', email: '', subject: 'general', message: '' });
    setTimeout(() => setSent(false), 5000);
  };

  const focusStyle = { borderColor: 'rgba(56,254,96,0.35)', boxShadow: '0 0 0 3px rgba(56,254,96,0.07)' };

  return (
    <div style={{ minHeight: '100vh', background: '#111113', color: '#fff', fontFamily: "'Space Grotesk', sans-serif", paddingTop: 88 }}>

            <div style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 24px 56px' }}>
        <FadeIn>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#38FE60', boxShadow: '0 0 10px #38FE60', display: 'inline-block' }} className="animate-pulse" />
            <span style={{ color: '#38FE60', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600 }}>
              Support
            </span>
          </div>
          <h1 style={{ fontSize: 'clamp(2.2rem, 5vw, 3.8rem)', fontWeight: 800, letterSpacing: '-0.045em', marginBottom: 14, lineHeight: 1.05 }}>
            Get in Touch
          </h1>
          <p style={{ color: '#555', fontSize: 15, maxWidth: 460, lineHeight: 1.75 }}>
            Questions, partnerships, or just want to say hello — we're here. Reach out through any channel below.
          </p>
        </FadeIn>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px 96px' }}>

        {/* ── Channel cards ─────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, marginBottom: 64 }}>
          {CHANNELS.map((ch, i) => <ChannelCard key={ch.label} ch={ch} delay={i * 60} />)}
        </div>

        {/* ── Main content: form + sidebar ──────────────────── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>

          {/* Contact Form */}
          <FadeIn delay={80} style={{ flex: '1 1 420px' }}>
            <div style={{
              borderRadius: 24,
              border: '1px solid rgba(255,255,255,0.07)',
              background: 'rgba(255,255,255,0.025)',
              overflow: 'hidden',
            }}>
              {/* Form header */}
              <div style={{
                padding: '28px 32px 20px',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                background: 'linear-gradient(135deg, rgba(56,254,96,0.03) 0%, transparent 50%)',
              }}>
                <h2 style={{ color: '#fff', fontSize: 17, fontWeight: 700, letterSpacing: '-0.025em', marginBottom: 5 }}>
                  Send a Message
                </h2>
                <p style={{ color: '#444', fontSize: 12 }}>We'll reply within 24 hours</p>
              </div>

                            <div style={{ padding: '28px 32px 32px' }}>
                {sent ? (
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    padding: '48px 0', gap: 16, textAlign: 'center',
                  }}>
                    <div style={{
                      width: 56, height: 56, borderRadius: '50%',
                      background: 'rgba(56,254,96,0.1)', border: '1px solid rgba(56,254,96,0.25)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <CheckCircle style={{ color: '#38FE60', width: 24, height: 24 }} />
                    </div>
                    <p style={{ color: '#fff', fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em' }}>Message sent!</p>
                    <p style={{ color: '#555', fontSize: 13 }}>We'll get back to you within 24 hours.</p>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      <Field label="Name">
                        <input
                          type="text" name="name" value={form.name} onChange={onChange} required
                          placeholder="Your name"
                          style={{ ...inputBase, ...(focused === 'name' ? focusStyle : {}) }}
                          onFocus={() => setFocused('name')} onBlur={() => setFocused(null)}
                        />
                      </Field>
                      <Field label="Email">
                        <input
                          type="email" name="email" value={form.email} onChange={onChange} required
                          placeholder="you@email.com"
                          style={{ ...inputBase, ...(focused === 'email' ? focusStyle : {}) }}
                          onFocus={() => setFocused('email')} onBlur={() => setFocused(null)}
                        />
                      </Field>
                    </div>

                    <Field label="Subject">
                      <CustomSelect
                        value={form.subject}
                        onChange={val => setForm(f => ({ ...f, subject: val }))}
                      />
                    </Field>

                    <Field label="Message">
                      <textarea
                        name="message" value={form.message} onChange={onChange} required
                        rows={5} placeholder="How can we help you?"
                        style={{ ...inputBase, resize: 'none', ...(focused === 'message' ? focusStyle : {}) }}
                        onFocus={() => setFocused('message')} onBlur={() => setFocused(null)}
                      />
                    </Field>

                    <button type="submit" style={{
                      width: '100%', padding: '14px',
                      background: '#38FE60', color: '#000',
                      border: 'none', borderRadius: 12,
                      fontSize: 13, fontWeight: 700,
                      fontFamily: "'Space Grotesk', sans-serif",
                      cursor: 'pointer', letterSpacing: '0.01em',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      boxShadow: '0 0 24px rgba(56,254,96,0.25)',
                      transition: 'box-shadow 0.2s, transform 0.15s',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 36px rgba(56,254,96,0.45)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 0 24px rgba(56,254,96,0.25)'; e.currentTarget.style.transform = 'none'; }}
                    >
                      <Send style={{ width: 15, height: 15 }} />
                      Send Message
                    </button>
                  </form>
                )}
              </div>
            </div>
          </FadeIn>

          {/* Sidebar */}
          <div style={{ flex: '0 1 300px', display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Response times */}
            <FadeIn delay={120}>
              <div style={{
                borderRadius: 22,
                border: '1px solid rgba(255,255,255,0.07)',
                background: 'rgba(255,255,255,0.025)',
                padding: '28px 28px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: 'rgba(56,254,96,0.08)', border: '1px solid rgba(56,254,96,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Clock style={{ color: '#38FE60', width: 16, height: 16 }} />
                  </div>
                  <p style={{ color: '#e0e0e0', fontSize: 14, fontWeight: 700, letterSpacing: '-0.02em' }}>Response Times</p>
                </div>
                {[
                  { channel: 'Telegram',     time: 'Instant',  color: '#38FE60' },
                  { channel: 'Discord',       time: '< 1 hour', color: '#a78bfa' },
                  { channel: 'Email',         time: '< 24 hrs', color: '#38bdf8' },
                ].map(r => (
                  <div key={r.channel} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                  }}>
                    <span style={{ color: '#666', fontSize: 12 }}>{r.channel}</span>
                    <span style={{ color: r.color, fontSize: 12, fontWeight: 700, fontFamily: 'monospace' }}>{r.time}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 16 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#38FE60', boxShadow: '0 0 6px #38FE60', display: 'inline-block' }} className="animate-pulse" />
                  <span style={{ color: '#444', fontSize: 11 }}>Support available 24 / 7</span>
                </div>
              </div>
            </FadeIn>

            {/* Social links */}
            <FadeIn delay={180}>
              <div style={{
                borderRadius: 22,
                border: '1px solid rgba(255,255,255,0.07)',
                background: 'rgba(255,255,255,0.025)',
                padding: '24px 28px',
              }}>
                <p style={{ color: '#888', fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 }}>Follow Us</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <SocialPill href="https://x.com/BullexAiLabs"          icon={<Twitter   style={{ width: 14, height: 14 }} />} label="Twitter"   />
                  <SocialPill href="https://www.instagram.com/bullexailabs/" icon={<Instagram style={{ width: 14, height: 14 }} />} label="Instagram" />
                  <SocialPill href="https://www.facebook.com/profile.php?id=61583845973970" icon={<Facebook  style={{ width: 14, height: 14 }} />} label="Facebook"  />
                  <SocialPill href="https://www.youtube.com/@bullexailabs/videos"  icon={<Youtube   style={{ width: 14, height: 14 }} />} label="YouTube"   />
                  <SocialPill href="https://t.me/bullexailabs"            icon={<Send      style={{ width: 14, height: 14 }} />} label="Telegram"  />
                  <SocialPill href="https://discord.gg/shdwXNAX"         icon={<MessageCircle style={{ width: 14, height: 14 }} />} label="Discord"  />
                </div>
              </div>
            </FadeIn>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
